import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  clearSession,
  createDevSession,
  loadSession,
  loginWithDemoAccount,
  isSessionUsable,
  loginWithPassword,
  msUntilSessionRefresh,
  refreshKeycloakSession,
  restoreSession,
  saveSession,
  type AuthConnectivityMode,
  type AuthSession,
  type DemoLoginResult,
  type LoginOptions,
} from "@/services/auth";
import { useUserContext } from "./UserContext";

export type SessionContextValue = {
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  refresh: () => void;
  establishDevSession: () => void;
  loginWithDemoAccount: (
    email: string,
    password: string,
    options?: LoginOptions,
  ) => Promise<DemoLoginResult>;
  loginWithPassword: (
    email: string,
    password: string,
    mode: AuthConnectivityMode,
    options?: LoginOptions,
  ) => Promise<DemoLoginResult>;
  clear: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { setUser } = useUserContext();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const sessionRef = useRef<AuthSession | null>(null);
  sessionRef.current = session;

  const applySession = useCallback(
    (next: AuthSession | null) => {
      setSession(next);
      setUser(next?.user ?? null);
    },
    [setUser],
  );

  const refresh = useCallback(() => {
    const next = loadSession();
    applySession(next);
    setIsLoading(false);
  }, [applySession]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next = await restoreSession();
      if (cancelled) return;
      applySession(next);
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  // Silent Keycloak renewal before access-token expiry (keeps session alive).
  useEffect(() => {
    if (!session?.refreshToken || session.source !== "keycloak") return;
    if (!session.expiresAt) return;

    const delay = msUntilSessionRefresh(session.expiresAt);
    const timer = window.setTimeout(() => {
      void (async () => {
        const current = sessionRef.current;
        if (!current?.refreshToken) return;
        const renewed = await refreshKeycloakSession(
          current.refreshToken,
          Boolean(current.persist),
        );
        if (renewed) {
          applySession(renewed);
          return;
        }
        clearSession();
        applySession(null);
      })();
    }, delay);

    return () => window.clearTimeout(timer);
  }, [
    session?.accessToken,
    session?.expiresAt,
    session?.refreshToken,
    session?.source,
    applySession,
  ]);

  const establishDevSession = useCallback(() => {
    const next = createDevSession();
    saveSession(next);
    applySession(next);
  }, [applySession]);

  const loginWithDemoAccountCallback = useCallback(
    async (email: string, password: string, options?: LoginOptions): Promise<DemoLoginResult> => {
      const result = await loginWithDemoAccount(email, password, options);
      if (result.ok) {
        if (!isSessionUsable(result.session)) {
          return { ok: false, error: "TOKEN_EXPIRED" };
        }
        saveSession(result.session);
        applySession(result.session);
      }
      return result;
    },
    [applySession],
  );

  const loginWithPasswordCallback = useCallback(
    async (
      email: string,
      password: string,
      mode: AuthConnectivityMode,
      options?: LoginOptions,
    ): Promise<DemoLoginResult> => {
      const result = await loginWithPassword(email, password, mode, options);
      if (result.ok) {
        if (!isSessionUsable(result.session)) {
          return { ok: false, error: "TOKEN_EXPIRED" };
        }
        saveSession(result.session);
        applySession(result.session);
      }
      return result;
    },
    [applySession],
  );

  const clear = useCallback(() => {
    clearSession();
    applySession(null);
  }, [applySession]);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      isAuthenticated: Boolean(session && isSessionUsable(session)),
      isLoading,
      refresh,
      establishDevSession,
      loginWithDemoAccount: loginWithDemoAccountCallback,
      loginWithPassword: loginWithPasswordCallback,
      clear,
    }),
    [
      session,
      isLoading,
      refresh,
      establishDevSession,
      loginWithDemoAccountCallback,
      loginWithPasswordCallback,
      clear,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionContext(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSessionContext must be used within SessionProvider");
  }
  return ctx;
}
